import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, FileText, Loader2 } from 'lucide-react';
import { api, type AfasSyncResult } from '../lib/api';
import { cn, formatDate } from '../lib/utils';
import { Card, Disclosure, ICON, Notice, Skeleton } from './ui';

const STATUS_LABEL: Record<string, { text: string; cls: string }> = {
  ok: { text: 'Gekoppeld', cls: 'status-ok' },
  needs_2fa: { text: 'Pocket-code nodig', cls: 'status-warn' },
  error: { text: 'Fout', cls: 'status-err' },
  never: { text: 'Nog niet opgehaald', cls: 'status-muted' },
  not_configured: { text: 'Niet ingesteld', cls: 'status-muted' },
};

const DOC_STATUS: Record<string, { text: string; cls: string }> = {
  imported: { text: 'Ingelezen', cls: 'status-ok' },
  present: { text: 'Stond er al', cls: 'status-muted' },
  error: { text: 'Fout', cls: 'status-err' },
  new: { text: 'Nieuw', cls: 'status-accent' },
};

const docLabel = (kind: string) => (kind === 'loonstrook' ? 'Loonstrook' : 'Urenbijlage');

/** AFAS InSite-koppeling: loonstroken, urenbijlages en gegevens automatisch ophalen (zie docs/07). */
export function AfasSection() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['afas-status'], queryFn: api.afas.status });
  const [code, setCode] = useState('');
  const [last, setLast] = useState<AfasSyncResult | null>(null);

  const done = (r: AfasSyncResult) => {
    setLast(r);
    setCode('');
    qc.invalidateQueries({ queryKey: ['afas-status'] });
    qc.invalidateQueries({ queryKey: ['acties-status'] });
    qc.invalidateQueries({ queryKey: ['dashboard'] });
  };
  const syncMut = useMutation({ mutationFn: api.afas.sync, onSuccess: done });
  const codeMut = useMutation({ mutationFn: (c: string) => api.afas.submit2fa(c), onSuccess: done });

  const st = STATUS_LABEL[data?.status ?? 'never'] ?? STATUS_LABEL.never;
  const busy = syncMut.isPending || codeMut.isPending;

  const resultText = (r: AfasSyncResult) => {
    if (r.status === 'needs_2fa') return 'AFAS vraagt om een Pocket-code.';
    if (r.status !== 'ok') return r.error ?? 'Onbekende fout';
    if (!r.imported.length) return 'Alles is bijgewerkt; er staat niets nieuws in AFAS.';
    const list = r.imported.map((i) => `${docLabel(i.kind).toLowerCase()} periode ${i.period} ${i.year}`).join(', ');
    return `Ingelezen: ${list}${r.remaining ? `. Nog ${r.remaining} te gaan.` : ''}`;
  };

  if (isLoading) {
    return (
      <div className="card space-y-3">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-4">
      <Card
        flush
        title="AFAS"
        icon={Download}
        action={
          <button type="button" className="btn-secondary btn-sm" disabled={busy || !data?.configured} onClick={() => syncMut.mutate()}>
            {syncMut.isPending && <Loader2 className={cn(ICON.sm, 'animate-spin')} aria-hidden />}
            {syncMut.isPending ? 'Bezig' : 'Ophalen'}
          </button>
        }
      >
        <p className="t-muted -mt-1 px-4 pb-3 sm:px-5">Haalt om de twee dagen je loonstroken, urenbijlages en gegevens op uit AFAS.</p>
        <div className="list border-t border-surface-line">
          <div className="row">
            <span className="flex-1 text-sm text-slate-300">Status</span>
            <span className={st.cls}>{st.text}</span>
          </div>
          {data?.last_sync_at && (
            <div className="row">
              <span className="flex-1 text-sm text-slate-300">Laatst opgehaald</span>
              <span className="t-num text-sm text-slate-100">{formatDate(data.last_sync_at, 'dd-MM-yyyy HH:mm')}</span>
            </div>
          )}
          {data?.trusted_until && (
            <div className="row">
              <span className="flex-1 text-sm text-slate-300">Geen Pocket-code nodig tot</span>
              <span className="t-num text-sm text-slate-100">{formatDate(data.trusted_until, 'dd-MM-yyyy')}</span>
            </div>
          )}
        </div>

        {(data?.last_error || (data && !data.configured) || last || data?.status === 'needs_2fa') && (
          <div className="space-y-3 border-t border-surface-line p-4 sm:p-5">
            {data?.last_error && <Notice tone="err">{data.last_error}</Notice>}
            {data && !data.configured && <Notice tone="warn">De AFAS-koppeling is nog niet ingesteld.</Notice>}

            {data?.status === 'needs_2fa' && (
              <form
                className="space-y-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  codeMut.mutate(code);
                }}
              >
                <label className="label" htmlFor="afas-code">Pocket-code</label>
                <p className="t-muted">Open AFAS Pocket, Tweestapsverificatie. Daarna blijft de koppeling 30 dagen actief.</p>
                <div className="flex gap-2">
                  <input
                    id="afas-code"
                    className="input t-num min-w-0 flex-1 text-center tracking-[0.3em]"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    placeholder="123456"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  />
                  <button type="submit" className="btn-primary shrink-0" disabled={code.length !== 6 || busy}>
                    {codeMut.isPending && <Loader2 className={cn(ICON.sm, 'animate-spin')} aria-hidden />}
                    Bevestigen
                  </button>
                </div>
              </form>
            )}

            {last && <Notice tone={last.status === 'ok' ? 'ok' : 'err'}>{resultText(last)}</Notice>}
          </div>
        )}
      </Card>

      {!!data?.documents.length && (
        <div className="card-flush enter">
          <Disclosure icon={FileText} title="Opgehaalde documenten" right={<span className="badge-count">{data.documents.length}</span>}>
            <div className="-mx-4 -mb-4 border-t border-surface-line/60 sm:-mx-5">
              <div className="list">
                {data.documents.map((d) => {
                  const s = DOC_STATUS[d.status] ?? { text: d.status, cls: 'status-muted' };
                  return (
                    <div key={`${d.kind}-${d.year}-${d.period_number}`} className="row">
                      <span className="min-w-0 flex-1 truncate text-sm text-slate-100">
                        {docLabel(d.kind)} periode {d.period_number} · {d.year}
                      </span>
                      <span className={s.cls} title={d.error ?? undefined}>{s.text}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </Disclosure>
        </div>
      )}
    </div>
  );
}
